// On-screen piano keyboard for /piano-drills — presentational and fully
// controlled by the parent: no internal note state, just a rendered range of
// keys that report pointer presses/releases and light up from `activeMidis`
// (user-held, from on-screen/qwerty/MIDI input) and `targetMidis` (the drill's
// guide highlight). Layout technique follows src/components/homeKey/Piano.js:
// white keys share width evenly in a flex-free absolute layout, black keys
// float on top at the boundary between their neighbors.
//
// Props: {
//   low, high: midi ints, inclusive,
//   activeMidis: Set<number>,
//   targetMidis: Set<number>,
//   onPress(midi), onRelease(midi),
//   kbHints?: { [midi]: "a" },
//   useFlats?: bool,
//   window?: { low, high } | null — when set, keys outside [low, high] render
//     dimmed/desaturated and an amber band marks the window's edges (the
//     pocket drill's "lit window"); null keeps today's plain render,
// }

import React, { useRef } from "react";
import { cn } from "../../lib/utils";
import { isBlackKey, midiToName } from "../../lib/pianoDrills";

const MIN_WHITE_KEY_PX = 28;
const WIDE_RANGE_SEMITONES = 36; // > 3 octaves

// Key list with a running white-key index for layout; each black key carries
// the index of the white key to its left (same scheme as homeKey Piano).
const buildKeys = (low, high) => {
  const keys = [];
  let wi = 0;
  for (let midi = low; midi <= high; midi += 1) {
    const black = isBlackKey(midi);
    keys.push({ midi, black, wi: black ? wi - 1 : wi });
    if (!black) wi += 1;
  }
  return keys;
};

export default function PianoRoll({
  low,
  high,
  activeMidis,
  targetMidis,
  onPress,
  onRelease,
  kbHints = {},
  useFlats = false,
  window = null,
}) {
  const downRef = useRef(new Set()); // midis this component is currently holding
  const active = activeMidis || new Set();
  const target = targetMidis || new Set();

  const keys = buildKeys(low, high);
  const whiteCount = keys.filter((k) => !k.black).length || 1;
  const W = 100 / whiteCount; // % width per white key
  const BW = W * 0.6; // black-key width
  const isWide = high - low > WIDE_RANGE_SEMITONES;
  const minWidthPx = whiteCount * MIN_WHITE_KEY_PX;

  // The pocket window band: left/right edges in the same %-of-row coordinates
  // the keys use, spanning every key inside [window.low, window.high].
  const inWindow = (midi) => !window || (midi >= window.low && midi <= window.high);
  let windowBand = null;
  if (window) {
    const inKeys = keys.filter((k) => inWindow(k.midi));
    if (inKeys.length) {
      const leftOf = (k) => (k.black ? (k.wi + 1) * W - BW / 2 : k.wi * W);
      const rightOf = (k) => (k.black ? (k.wi + 1) * W + BW / 2 : (k.wi + 1) * W);
      const left = Math.min(...inKeys.map(leftOf));
      windowBand = { left, width: Math.max(...inKeys.map(rightOf)) - left };
    }
  }

  const start = (midi) => {
    if (downRef.current.has(midi)) return;
    downRef.current.add(midi);
    if (onPress) onPress(midi);
  };
  const end = (midi) => {
    if (!downRef.current.has(midi)) return;
    downRef.current.delete(midi);
    if (onRelease) onRelease(midi);
  };

  const handlePointerDown = (midi) => (e) => {
    e.preventDefault();
    // Keep this pointer bound to its key so the matching up/cancel lands
    // back here even if the finger drifts off — no stuck notes.
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        // stale pointer id — harmless
      }
    }
    start(midi);
  };
  const handleEnd = (midi) => () => end(midi);

  return (
    <div className={cn(isWide && "overflow-x-auto")}>
      <div
        className="piano-roll relative h-32 sm:h-40"
        style={isWide ? { minWidth: `${minWidthPx}px` } : undefined}
        role="group"
        aria-label={`piano keyboard, ${midiToName(low, useFlats)} to ${midiToName(high, useFlats)}`}
      >
        {keys.map((k) => {
          const left = k.black ? (k.wi + 1) * W - BW / 2 : k.wi * W;
          const width = k.black ? BW : W;
          const isActive = active.has(k.midi);
          const isTarget = target.has(k.midi);
          const showName = k.midi % 12 === 0; // every C
          const hint = kbHints[k.midi];

          return (
            <button
              key={k.midi}
              type="button"
              data-midi={k.midi}
              className={cn(
                "piano-roll__key touch-none select-none absolute top-0 flex flex-col items-center justify-end pb-1 rounded-b-md border transition-transform duration-75",
                k.black
                  ? "piano-roll__key--black z-10 h-20 sm:h-24 bg-slate-900 border-slate-950 text-slate-300"
                  : "piano-roll__key--white z-0 h-32 sm:h-40 bg-white border-slate-300 text-slate-500",
                window &&
                  !inWindow(k.midi) &&
                  cn(
                    "piano-roll__key--dim",
                    k.black
                      ? "bg-slate-500 border-slate-400 text-slate-400"
                      : "bg-slate-100 border-slate-200 text-slate-300"
                  ),
                isActive &&
                  !isTarget &&
                  cn(
                    "piano-roll__key--active",
                    k.black
                      ? "bg-indigo-500 border-indigo-600 text-white translate-y-0.5"
                      : "bg-sky-300 border-sky-500 text-slate-900 translate-y-0.5"
                  ),
                isTarget &&
                  !isActive &&
                  cn(
                    "piano-roll__key--target ring-2 ring-inset ring-emerald-400",
                    k.black ? "bg-slate-800" : "bg-emerald-50"
                  ),
                isActive &&
                  isTarget &&
                  cn(
                    "piano-roll__key--both ring-2 ring-inset ring-emerald-400 translate-y-0.5",
                    k.black
                      ? "bg-teal-500 border-teal-400 text-white"
                      : "bg-teal-300 border-teal-500 text-slate-900"
                  )
              )}
              style={{ left: `${left}%`, width: `${width}%` }}
              aria-label={midiToName(k.midi, useFlats)}
              aria-pressed={isActive}
              onPointerDown={handlePointerDown(k.midi)}
              onPointerUp={handleEnd(k.midi)}
              onPointerCancel={handleEnd(k.midi)}
              onPointerLeave={handleEnd(k.midi)}
            >
              {showName && (
                <span className="text-[10px] font-medium leading-none pointer-events-none">
                  {midiToName(k.midi, useFlats)}
                </span>
              )}
              {hint && (
                <span className="text-[9px] font-mono text-slate-400 leading-none mt-0.5 pointer-events-none">
                  {hint}
                </span>
              )}
            </button>
          );
        })}
        {windowBand && (
          <div
            aria-hidden
            className="piano-roll__window pointer-events-none absolute inset-y-0 z-20 rounded-md border-x-2 border-amber-400/90 bg-amber-200/10"
            style={{ left: `${windowBand.left}%`, width: `${windowBand.width}%` }}
          />
        )}
      </div>
    </div>
  );
}
